import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { colors, glyphs, useMediaQuery } from "../../../Styles";
import { NewSelect } from "../..";
import { UndefinedOptions, useFeedcurveOptions, useGroupOptions, useMilestoneOptions, useSectionOptions, useSystemOptions } from "../../_Components";
import { field, FilterContent, NewFieldStyles, QuickEntry, ValueType, createDisplayTypesFromOptions, getLabelFromKey } from "../../../Definitions";

type QuickEntryModalLvl1Props = {
  quickEntryData: QuickEntry[];
  onQuickEntryDataChange: (data: QuickEntry[]) => void;
  focus_view?: boolean
};

export default function QuickEntryModalLvl1({quickEntryData, onQuickEntryDataChange, focus_view=false}:QuickEntryModalLvl1Props) {
  const {t} = useTranslation()
  const groupOptions = useGroupOptions();
  const { feedcurveOptions } = useFeedcurveOptions();
  const milestoneOptions = useMilestoneOptions();
  const systemOptions = useSystemOptions();
  const { allSectionOptions, locationOptions } = useSectionOptions();
  const [selectFields, setSelectFields] = useState<field[]>([{ id: -1 }]);
  const mobileXs = useMediaQuery(`(max-width: 793px)`) || focus_view;

  // #region | Select Parameters
  const selectParameters = createDisplayTypesFromOptions({groupOptions, feedcurveOptions, milestoneOptions, systemOptions, allSectionOptions, locationOptions})

  const filterParamOptions = selectParameters.map((type) => ({
    label: t(`params.${type.label}`, {defaultValue: type.label}), 
    id: type.id
  }));
  // #endregion

  // If quickEntryData already have values to show
  useEffect(() => {
    if (quickEntryData && quickEntryData.length > 0) {
      const updatedFields: field[] = quickEntryData.map((param) => {
        const label = getLabelFromKey(param.key); 
        const id = selectParameters.find((p) => p.label === label)?.id ?? -1;
        return { id, value: param.value };
      });
      updatedFields.push({ id: -1 });
      setSelectFields(updatedFields);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [quickEntryData]);
  
  // Handle filter type change
  const handleFilterTypeChange = (index: number, id: number ) => {
    const updatedSelectFields = [...selectFields];
    if (updatedSelectFields[index].id === -1) {
      updatedSelectFields.push({ id: -1 });
    }
    updatedSelectFields[index] = { id: id, value: undefined };

    const selectedParameter = selectParameters.find((param) => param.id === id);
    if (selectedParameter) {
      const updatedFilterParameter = [...quickEntryData];
        updatedFilterParameter[index] = {
          key: selectedParameter.key,
          value: []
        };
      onQuickEntryDataChange(updatedFilterParameter);
    }
  setSelectFields(updatedSelectFields);
  };

  // Handle filter value change
  const handleFilterValueChange = (index: number, values: ValueType[]) => {
    const updatedFilterParameter = Array.isArray(quickEntryData) ? [...quickEntryData] : [];
  
    if (!updatedFilterParameter[index]) return;
  
    updatedFilterParameter[index].value = values;
    // console.log(values, updatedFilterParameter); 
    onQuickEntryDataChange(updatedFilterParameter);
  };

  // Remove a SelectField by index
  const handleRemoveField = useCallback((index: number) => {
    const updatedSelectFields = [...selectFields];
    updatedSelectFields.splice(index, 1);
    setSelectFields(updatedSelectFields);
  
    const updatedFilterParameter = quickEntryData.filter((_, i) => i !== index);
  
    onQuickEntryDataChange(updatedFilterParameter);
  }, [onQuickEntryDataChange, quickEntryData, selectFields]);

  // console.log(quickEntryData);
  
  // #region | Rendered Fields
  const renderedFields = selectFields.map((field, index) => {
    // Remove already used options from optionslist
    const selectedIds = selectFields
    .filter((f, i) => i !== index && f.id !== undefined)
    .map((f) => f.id); 
    const availableOptions = filterParamOptions.filter((option) => !selectedIds.includes(option.id));

    return (
      <div key={field.id !== undefined ? `${field.id}-${index}` : index} style={{ 
        width: "100%", 
        display: "flex", 
        flexDirection: "column", 
        gap: "10px", 
        marginBottom: index === selectFields.length - 1 ? "0px" : "30px"
      }}>
        <div style={{ display: "flex", flexDirection: "row", gap: "20px", alignItems: "center", justifyContent: "space-between" }}>
          <NewSelect
            placeholder={t("select", {defaultValue: "select"})}
            key={index+String(field.id)}
            options={availableOptions}
            onChange={(option) => {
              option?.id && handleFilterTypeChange(index, Number(option.id));
            }}
            fieldOverrides={{
              fieldWidth: mobileXs ? "250px" : "280px",
              fieldStyle: focus_view ? NewFieldStyles.greyDarkPrimary : NewFieldStyles.blueDarkSecondary,
              focus_view: focus_view
            }}
            valueOverrides={{
              defaultValue: filterParamOptions.find((parameter) => {return parameter.id === field.id})
            }}
          />
          {field.id !== -1 && ( <>
            <div style={{cursor: "pointer"}} onClick={() => handleRemoveField(index)}>
              <glyphs.Delete size={focus_view ? "30px" : "25px"}/>
            </div>
          </> )}
        </div>
        {/* Content based on selected filter parameter */}
        {field.id !== -1 && (
          <div style={{    
            width: "100%",
            height: "fit-content",
            boxSizing: "border-box",
            backgroundColor: focus_view ? colors.greyPrimary : colors.greyLightSecondary,
            padding: "10px",
            borderRadius: "10px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}>
            <FilterContent 
              filter={field} 
              index={index} 
              filterParameterTypes={selectParameters} 
              handleFilterValueChange={(values) => {handleFilterValueChange(index, values)}}
              focus_view={focus_view}
            />
          </div>
        )}
      </div>
    )
  })
  // #endregion
  
  return (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "start",
      flexDirection: "column",
      boxSizing: "border-box"
    }}>
      {renderedFields}
    </div>
  )
}